import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsAdmin } from '@/features/auth/hooks';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AgentRunCard } from '@/components/AgentRunCard';
import { AgentPollingPanel } from '@/components/AgentPollingPanel';
import { ScrollToTopButton } from '@/components/ScrollToTopButton';
import { ApiError } from '@/lib/api-client';
import {
  useDispatchRegulatoryStrategyReview,
  useRegulatoryStrategyReviewRun,
} from '@/features/simulator/hooks';
import type {
  Pathway,
  StrategyReviewRunDetail,
} from '@/features/simulator/api';
import { StrategyReviewResultRenderer } from '@/features/simulator/components/StrategyReviewResultRenderer';
import { PageHero } from '@/components/layout/PageHero';
import { RunLoadingSkeleton } from '@/components/RunLoadingSkeleton';

/**
 * Regulatory Strategy Reviewer route. URL:
 *
 *   /simulators/regulatory-strategy[?run_id=<n>]
 *
 * Two modes (mirrors the clinical-protocol simulator's shape):
 *   - No run_id  → upload form (.docx OR .pdf + product-type picker → POST dispatch).
 *   - run_id set → poll the run, show stepper while running, render
 *                  the full review report once complete.
 */

const searchSchema = z.object({
  run_id: z.coerce.number().int().positive().optional().catch(undefined),
});

const PRODUCT_TYPE_OPTIONS: { value: Pathway; label: string }[] = [
  { value: 'drug', label: 'Drug (IND → NDA / 505(b)(2) / ANDA)' },
  { value: 'biologic', label: 'Biologic (IND → BLA / 351(k))' },
  { value: 'device', label: 'Medical Device (IDE → 510(k) / De Novo / PMA)' },
  { value: 'combination', label: 'Combination product (drug + device rules)' },
];

const STRATEGY_WORKFLOW_STEPS = [
  'Extract strategy document text',
  'Identify product type + filing pathway',
  'Query openFDA + FDA guidance database',
  'Score strategy against pre-submission expectations',
  'Classify Critical / Major / Minor',
  'Render strategy review report',
] as const;

const STRATEGY_STEP_SECONDS = 12;

export const Route = createFileRoute('/simulators_/regulatory-strategy')({
  validateSearch: searchSchema,
  component: RegulatoryStrategyReviewerPage,
});

function RegulatoryStrategyReviewerPage() {
  const { run_id: runId } = Route.useSearch();
  const navigate = useNavigate();
  const runQuery = useRegulatoryStrategyReviewRun(runId ?? null);

  return (
    <>
      <PageHero
        title="Regulatory Strategy Reviewer"
        subtitle="Pre-screen a regulatory strategy document against FDA pre-submission engagement expectations."
      />

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-20 space-y-5">
        {runId === undefined && (
          <UploadPanel
            onDispatched={(newRunId) => {
              void navigate({
                to: '/simulators/regulatory-strategy',
                search: { run_id: newRunId },
              });
            }}
          />
        )}

        {runId !== undefined && runQuery.isLoading && (
          <RunLoadingSkeleton />
        )}

        {runId !== undefined && runQuery.isError && (
          <ErrorCard message="Could not load this strategy review. It may have been deleted, or you may not have access." />
        )}

        {runId !== undefined && runQuery.data && (
          <RunStateBody
            run={runQuery.data}
            onStartAnother={() => void navigate({ to: '/simulators/regulatory-strategy' })}
          />
        )}
      </main>
    </>
  );
}

// ── Upload panel (no run_id mode) ───────────────────────────────────────

function UploadPanel({
  onDispatched,
}: {
  onDispatched: (runId: number) => void;
}) {
  const dispatch = useDispatchRegulatoryStrategyReview();
  const isAdmin = useIsAdmin();
  const [productType, setProductType] = useState<Pathway>('drug');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError('Pick a .docx or .pdf file to upload.');
      return;
    }
    try {
      const res = await dispatch.mutateAsync({ file, productType });
      onDispatched(res.strategy_review_run_id);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Dispatch failed.',
      );
    }
  };

  return (
    <section className="bg-white border border-line rounded-2xl p-7 shadow-card">
      <h2 className="text-[18px] font-bold text-navy m-0 mb-2">
        Upload strategy document
      </h2>
      <p className="text-[14px] text-muted leading-[1.55] m-0 mb-4">
        Pick the product type, then upload your draft regulatory strategy
        as a <strong className="text-navy font-semibold">.docx</strong> or
        <strong className="text-navy font-semibold"> .pdf</strong>. An AI
        reviewer consults openFDA + FDA guidance and returns findings
        classified Critical / Major / Minor with regulation citations.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted">
            Product type
          </label>
          <Select value={productType} onValueChange={(v) => setProductType(v as Pathway)}>
            <SelectTrigger className="w-full max-w-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRODUCT_TYPE_OPTIONS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept=".docx,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="flex-1 min-w-0 py-2.5 px-3 border border-line-strong rounded-md bg-white text-[14px] text-ink file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-electric/10 file:text-navy file:font-semibold file:text-[12px] file:cursor-pointer hover:file:bg-electric/15"
          />
          <Button
            type="submit"
            variant="brand"
            disabled={dispatch.isPending || !file || !isAdmin}
            title={!isAdmin ? 'Admin only' : undefined}
            className="min-w-[160px]"
          >
            {dispatch.isPending ? 'Running review…' : 'Run review'}
          </Button>
        </div>

        <p className="text-[12px] text-muted m-0">
          .docx or .pdf only. Max 10 MB.
        </p>

        {error && (
          <div
            role="alert"
            className="rounded-[10px] py-3 px-4 bg-state-error-bg border border-state-error-border text-state-error-text text-[14px] flex items-start gap-2.5"
          >
            <AlertCircle className="size-4 shrink-0 mt-0.5" />
            {error}
          </div>
        )}
      </form>
    </section>
  );
}

// ── Run-state body ──────────────────────────────────────────────────────

function RunStateBody({
  run,
  onStartAnother,
}: {
  run: StrategyReviewRunDetail;
  onStartAnother: () => void;
}) {
  if (run.status === 'queued' || run.status === 'running') {
    return (
      <>
        <AgentRunCard
          run={{
            id: run.id,
            status: run.status,
            started_at: run.started_at,
          }}
          agentLabel="Strategy review run"
        />
        <AgentPollingPanel
          startedAt={run.started_at}
          workflowSteps={STRATEGY_WORKFLOW_STEPS}
          stepSeconds={STRATEGY_STEP_SECONDS}
          title="Reviewing strategy"
          subtitle="Reading the document, consulting openFDA + FDA guidance, and classifying findings against pre-submission expectations."
        />
      </>
    );
  }

  if (run.status === 'failed') {
    return (
      <div className="bg-state-error-bg border-l-4 border-state-error-accent rounded-lg py-[18px] px-6 text-state-error-text">
        <h3 className="m-0 mb-1.5 text-[17px] font-bold">
          The review didn&rsquo;t complete
        </h3>
        <p className="text-[14px] leading-[1.55] m-0 mb-4">
          {run.error_message ||
            'The strategy reviewer did not complete. Try running it again with the same or a revised file.'}
        </p>
        <Button variant="outline" onClick={onStartAnother}>
          Run another strategy
        </Button>
      </div>
    );
  }

  if (run.strategy_review_response) {
    return (
      <>
        <AgentRunCard
          run={{
            id: run.id,
            status: run.status,
            started_at: run.started_at,
          }}
          agentLabel="Strategy review run"
        />
        <StrategyReviewResultRenderer report={run.strategy_review_response} />
        <ScrollToTopButton />
        <div className="flex justify-center pt-2">
          <Button variant="outline" onClick={onStartAnother}>
            Run another strategy
          </Button>
        </div>
      </>
    );
  }

  return (
    <div className="bg-state-warning-bg border border-state-warning-border rounded-2xl p-6 text-state-warning-text">
      <div className="font-bold text-[17px] mb-1">
        Run completed but no report was produced
      </div>
      <p className="text-[14px] m-0 mb-3">
        The agent finished but the response payload was missing or
        unparseable. Try uploading the document again.
      </p>
      <Button variant="outline" onClick={onStartAnother}>
        Run another strategy
      </Button>
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-6 text-state-error-text">
      {message}
    </div>
  );
}
